import { useEffect, useId, useRef, useState } from 'react'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'

export const PROGRAMS = ['Fusion', 'Ansys', 'KiCad', 'ArchiCAD', 'AutoCAD', 'Altium', 'SolidWorks', 'Siemens NX', 'Physics', 'MATLAB']
const normalize = (s: string) => s.normalize('NFKD').toLowerCase().replace(/[^\p{L}\p{N}+#]/gu, '')
const aliases: Record<string, string> = { fusion360: 'Fusion', autodeskfusion360: 'Fusion', microsoftexcel: 'Excel', mechanicalengineer: 'Mechanical engineering', architect: 'Architecture', pcb: 'Electronics engineering' }
export const taskPrograms = (task: { programs?: string[]; program?: string }) => task.programs ?? (task.program ? [task.program] : [])
export const programLabel = (task: { programs?: string[]; program?: string }) => taskPrograms(task).join(', ')
/** The program of the science pipeline: every template and the workflows built from it. */
export const SCIENCE_PROGRAM = 'Physics'

export const isScienceExpert = (profile: { programs?: string[] } | null) =>
  !!profile?.programs?.some(p => p.toLowerCase() === SCIENCE_PROGRAM.toLowerCase())

/** Search shared labels and add a custom value. Enter selects a close match first. */
export function CataloguePicker({ kind = 'program', value, onChange, label, preferred = [], disabled = false }: {
  kind?: 'area' | 'program'; value: string[]; onChange: (value: string[]) => void; label: string; preferred?: string[]; disabled?: boolean
}) {
  const [choices, setChoices] = useState<string[]>(kind === 'program' ? PROGRAMS : [])
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [keyboardActive, setKeyboardActive] = useState(false)
  const [error, setError] = useState('')
  const id = useId()
  const input = useRef<HTMLInputElement>(null)
  const limit = kind === 'area' ? 5 : 20
  useEffect(() => {
    let alive = true
    api.get<{ areas: string[]; programs: string[] }>('/profile-options')
      .then(data => { if (alive) setChoices(kind === 'area' ? data.areas : data.programs) })
      .catch(() => { if (alive) setError('Suggestions are unavailable. You can enter a name.') })
    return () => { alive = false }
  }, [kind])
  const text = query.trim().replace(/\s+/g, ' ')
  const needle = normalize(aliases[normalize(text)] ?? text)
  const unique = [...new Map([...choices, ...preferred, ...value].map(v => [normalize(v), v])).values()]
  // Token matches find reordered names; edit distance finds common spelling mistakes.
  function distance(a: string, b: string) {
    let previous = Array.from({ length: b.length + 1 }, (_, i) => i)
    let beforePrevious = previous
    for (let i = 1; i <= a.length; i++) {
      const row = [i]
      for (let j = 1; j <= b.length; j++) {
        row[j] = Math.min(previous[j] + 1, row[j - 1] + 1, previous[j - 1] + Number(a[i - 1] !== b[j - 1]))
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
          row[j] = Math.min(row[j], beforePrevious[j - 2] + 1)
      }
      beforePrevious = previous; previous = row
    }
    return previous[b.length]
  }
  const score = (v: string) => {
    const name = normalize(v)
    if (!needle) return preferred.includes(v) ? 0 : 1
    if (name === needle) return 0
    if (name.startsWith(needle)) return 1
    if (name.includes(needle) || text.toLowerCase().split(/\s+/).every(token => v.toLowerCase().includes(token))) return 2
    return needle.length >= 4 && distance(needle, name) <= Math.max(1, Math.floor(needle.length * .2)) ? 3 : 9
  }
  const matches = unique.filter(v => score(v) < 9).sort((a, b) => score(a) - score(b) || a.localeCompare(b)).slice(0, 12)
  const exact = unique.find(v => normalize(v) === needle)
  const options = [...matches, ...(text && !exact ? [`Add “${text}”`] : [])]
  function choose(index: number, toggle = false) {
    if (disabled) return
    const item = matches[index] ?? exact ?? text
    if (!normalize(item)) return
    const selected = value.some(v => normalize(v) === normalize(item))
    if (!selected && value.length >= limit) return
    if (selected && toggle) onChange(value.filter(v => normalize(v) !== normalize(item)))
    if (!selected) onChange([...value, item])
    setQuery(''); setOpen(true); setActive(0); setKeyboardActive(false)
    input.current?.focus()
  }
  return <div className="catalogue-picker" onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false) }}>
    <div className="chip-list">{value.map(v => <button type="button" className="chip" key={v} disabled={disabled} aria-label={`Remove ${v}`} onClick={() => onChange(value.filter(item => item !== v))}>{v} ×</button>)}</div>
    <input ref={input} type="search" disabled={disabled} aria-label={label} role="combobox" aria-autocomplete="list" aria-expanded={open && !disabled} aria-controls={id}
      aria-activedescendant={open && options[active] ? `${id}-${active}` : undefined} placeholder={`Search ${kind === 'area' ? 'areas or professions' : 'programs'} or add one…`}
      value={query} maxLength={120} onFocus={() => { setOpen(true); setKeyboardActive(false) }}
      onChange={e => { setQuery(e.target.value); setOpen(true); setActive(0); setKeyboardActive(false) }} onKeyDown={e => {
        if (e.key === 'Escape') { setOpen(false); return }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setKeyboardActive(true); setActive(i => (i + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % Math.max(1, options.length)) }
        if (e.key === 'Enter' && open && options.length) { e.preventDefault(); choose(active) }
      }} />
    {open && !disabled && <div className="catalogue-options"><div id={id} role="listbox" aria-label={label} aria-multiselectable="true">{options.map((v, i) => {
      const selected = i < matches.length && value.includes(v)
      return <button key={v} type="button" role="option" id={`${id}-${i}`} aria-selected={selected} className={keyboardActive && i === active ? 'active' : ''}
        disabled={!selected && value.length >= limit} onMouseDown={e => e.preventDefault()} onClick={() => choose(i, true)}>
        <span className={`catalogue-check${selected ? ' checked' : ''}`}>{selected && <Icon name="check" size={13} />}</span><span>{v}</span>
      </button>
    })}</div><div className="catalogue-footer"><p className="catalogue-help" role="status">{value.length} of {limit} selected{value.length >= limit ? ' · Remove one to add another.' : ''}</p><button type="button" className="sm" onMouseDown={e => e.preventDefault()} onClick={() => setOpen(false)}>Done</button></div></div>}
    {error && <p className="small muted" role="status">{error}</p>}
  </div>
}
export function ProgramChips({ value, onChange, preferred, disabled }: { value: string[]; onChange: (v: string[]) => void; preferred?: string[]; disabled?: boolean }) {
  return <CataloguePicker value={value} onChange={onChange} preferred={preferred} disabled={disabled} label="Software and tools" />
}
export function AreaPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  return <CataloguePicker kind="area" value={value} onChange={onChange} label="Areas / professions" />
}
